if (!customElements.get('l20-eleven-qualification')) {
  class L20ElevenQualification extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-qualification-slide]')];
      this.controls = [...this.querySelectorAll('[data-qualification-control]')];
      this.slidesTrack = this.querySelector('.l20-eleven-qualification__slides');
      this.liveRegion = this.querySelector('[data-qualification-live]');
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Number.parseInt(this.dataset.autoplayDelay, 10) || 5000;
      this.desktopQuery = window.matchMedia('(min-width: 769px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isIntersecting = true;
      this.pointerInside = false;
      this.focusInside = false;
      this.editorPaused = false;
      this.manualPaused = false;
      this.isDesktopLayout = this.desktopQuery.matches;
      this.mobileScrollTimer = null;
      this.controlsAlignRaf = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handleMobileScroll = this.handleMobileScroll.bind(this);
      this.scheduleControlsAlignment = this.scheduleControlsAlignment.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.desktopQuery.addEventListener('change', this.handleMediaChange);
      this.reducedMotionQuery.addEventListener('change', this.handleMediaChange);
      this.slidesTrack?.addEventListener('scroll', this.handleMobileScroll, { passive: true });
      this.addEventListener('load', this.scheduleControlsAlignment, true);

      if ('ResizeObserver' in window) {
        this.controlsResizeObserver = new ResizeObserver(this.scheduleControlsAlignment);
        this.controlsResizeObserver.observe(this);
        this.slides.forEach((slide) => this.controlsResizeObserver.observe(slide));
      } else {
        window.addEventListener('resize', this.scheduleControlsAlignment);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.isIntersecting = entry.isIntersecting;
            if (entry.isIntersecting) this.startAutoplay();
            else this.stopAutoplay();
          },
          { threshold: 0.2 },
        );
        this.observer.observe(this);
      }

      this.syncLayoutMode('initial');
      this.scheduleControlsAlignment();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.mobileScrollTimer) {
        window.clearTimeout(this.mobileScrollTimer);
        this.mobileScrollTimer = null;
      }
      this.slidesTrack?.removeEventListener('scroll', this.handleMobileScroll);
      this.removeEventListener('load', this.scheduleControlsAlignment, true);
      this.controlsResizeObserver?.disconnect();
      window.removeEventListener('resize', this.scheduleControlsAlignment);
      if (this.controlsAlignRaf) window.cancelAnimationFrame(this.controlsAlignRaf);
      this.observer?.disconnect();
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.desktopQuery?.removeEventListener('change', this.handleMediaChange);
      this.reducedMotionQuery?.removeEventListener('change', this.handleMediaChange);
    }

    handleClick(event) {
      const control = event.target.closest('[data-qualification-control]');
      if (!control || !this.contains(control)) return;

      this.showSlide(Number.parseInt(control.dataset.slideIndex, 10), 'user');
    }

    handleKeydown(event) {
      const control = event.target.closest('[data-qualification-control]');
      if (!control || !this.contains(control)) return;

      let nextIndex;
      if (event.key === 'ArrowRight') nextIndex = (this.currentIndex + 1) % this.slides.length;
      if (event.key === 'ArrowLeft') nextIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.slides.length - 1;
      if (nextIndex === undefined) return;

      event.preventDefault();
      this.showSlide(nextIndex, 'user');
      this.controls[nextIndex]?.focus();
    }

    handleMouseEnter() {
      this.pointerInside = true;
      this.stopAutoplay();
    }

    handleMouseLeave() {
      this.pointerInside = false;
      this.startAutoplay();
    }

    handleFocusIn() {
      this.focusInside = true;
      this.stopAutoplay();
    }

    handleFocusOut() {
      requestAnimationFrame(() => {
        this.focusInside = this.contains(document.activeElement);
        if (!this.focusInside) this.startAutoplay();
      });
    }

    handleVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    handleMediaChange() {
      this.isDesktopLayout = this.desktopQuery.matches;
      if (this.mobileScrollTimer) {
        window.clearTimeout(this.mobileScrollTimer);
        this.mobileScrollTimer = null;
      }
      this.syncLayoutMode('media');
      this.startAutoplay();
    }

    handleBlockSelect(event) {
      const slide = event.target.closest('[data-qualification-slide]');
      if (!slide || !this.contains(slide)) return;

      this.editorPaused = true;
      this.stopAutoplay();
      this.showSlide(Number.parseInt(slide.dataset.slideIndex, 10), 'editor');
    }

    handleBlockDeselect() {
      this.editorPaused = false;
      this.startAutoplay();
    }

    showSlide(index, source) {
      if (!this.slides.length || Number.isNaN(index)) return;

      const normalizedIndex = (index + this.slides.length) % this.slides.length;
      this.currentIndex = normalizedIndex;
      this.renderSlideState(normalizedIndex);

      if (!this.isDesktopLayout) {
        const behavior = source === 'user' ? 'smooth' : 'auto';
        this.scrollToSlide(normalizedIndex, behavior);
      }

      if (source === 'user' && this.liveRegion) {
        const label = this.controls[normalizedIndex]?.dataset.slideLabel || `Qualification ${normalizedIndex + 1}`;
        this.liveRegion.textContent = `${label} selected. Automatic rotation paused.`;
      }

      if (source === 'user') {
        this.manualPaused = true;
        this.stopAutoplay();
      }
    }

    syncLayoutMode(source) {
      if (!this.slides.length) return;

      if (this.isDesktopLayout) {
        if (this.mobileScrollRaf) {
          window.cancelAnimationFrame(this.mobileScrollRaf);
          this.mobileScrollRaf = null;
        }

        this.renderSlideState(this.currentIndex);
        this.slides.forEach((slide, slideIndex) => {
          slide.setAttribute('aria-hidden', String(slideIndex !== this.currentIndex));
        });
        return;
      }

      this.slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', String(slideIndex !== this.currentIndex));
      });
      this.renderSlideState(this.currentIndex);
      this.scrollToSlide(this.currentIndex, source === 'initial' ? 'auto' : 'smooth');
    }

    renderSlideState(normalizedIndex) {
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      this.controls.forEach((control, controlIndex) => {
        const isActive = controlIndex === normalizedIndex;
        control.classList.toggle('is-active', isActive);
        control.setAttribute('aria-current', String(isActive));
        control.tabIndex = isActive ? 0 : -1;
      });

      this.scheduleControlsAlignment();
    }

    scheduleControlsAlignment() {
      if (!this.isDesktopLayout) return;
      if (this.controlsAlignRaf) window.cancelAnimationFrame(this.controlsAlignRaf);

      this.controlsAlignRaf = window.requestAnimationFrame(() => {
        this.controlsAlignRaf = null;
        this.alignControlsWithSignature();
      });
    }

    alignControlsWithSignature() {
      if (!this.isDesktopLayout) return;
      const controlsGroup = this.querySelector('.l20-eleven-qualification__controls');
      const activeSlide = this.slides[this.currentIndex];
      const signature = activeSlide?.querySelector('.l20-eleven-qualification__signature');
      const controlLine = controlsGroup?.querySelector('.l20-eleven-qualification__control span');

      if (!controlsGroup || !signature || !controlLine) {
        controlsGroup?.style.removeProperty('--l20-eleven-controls-offset-y');
        return;
      }

      const currentOffset = Number.parseFloat(
        controlsGroup.style.getPropertyValue('--l20-eleven-controls-offset-y'),
      ) || 0;
      const signatureBottom = signature.getBoundingClientRect().bottom;
      const controlLineBottom = controlLine.getBoundingClientRect().bottom;
      const nextOffset = currentOffset + signatureBottom - controlLineBottom;

      controlsGroup.style.setProperty('--l20-eleven-controls-offset-y', `${nextOffset.toFixed(2)}px`);
    }

    scrollToSlide(index, behavior = 'auto') {
      if (!this.slidesTrack || !this.slides.length) return;

      const slideWidth = this.slidesTrack.clientWidth;
      if (!slideWidth) return;

      this.slidesTrack.scrollTo({
        left: slideWidth * index,
        behavior
      });
    }

    handleMobileScroll() {
      if (this.isDesktopLayout || !this.slidesTrack || !this.slides.length) return;

      if (this.mobileScrollTimer) {
        window.clearTimeout(this.mobileScrollTimer);
      }

      this.mobileScrollTimer = window.setTimeout(() => {
        this.mobileScrollTimer = null;
        const slideWidth = this.slidesTrack.clientWidth;
        if (!slideWidth) return;

        const nextIndex = Math.round(this.slidesTrack.scrollLeft / slideWidth);
        const normalizedIndex = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
        if (normalizedIndex === this.currentIndex) return;

        this.currentIndex = normalizedIndex;
        this.renderSlideState(normalizedIndex);
      }, 120);
    }

    startAutoplay() {
      this.stopAutoplay();

      const canAutoplay =
        this.autoplayEnabled &&
        this.slides.length > 1 &&
        this.desktopQuery.matches &&
        !this.reducedMotionQuery.matches &&
        this.isIntersecting &&
        !this.pointerInside &&
        !this.focusInside &&
        !this.editorPaused &&
        !this.manualPaused &&
        !document.hidden;

      if (!canAutoplay) return;

      this.autoplayTimer = window.setInterval(() => {
        this.showSlide(this.currentIndex + 1, 'autoplay');
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('l20-eleven-qualification', L20ElevenQualification);
}
